import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FiTrash2, FiUpload } from "react-icons/fi";
import { useAppSelector } from "../../../../../redux/hooks";
import {
  selectSelectedLeague,
  selectSelectedLeagueId,
} from "../../../../../redux/slice/selectedLeagueSlice";
import { QUERY_KEY } from "../../../../../services/queryEndPoint";
import { Api } from "../../../../../services/interceptor";
import { Images } from "../../../../../utils/imageConstants";
import ImageZoomModal from "../../../../../components/common/ImageZoomModal";
import { getAuthToken, getGuestModeFromStorage } from "../../../../../helper/helper";
import { fileToBase64, splitBase64 } from "../../../../../utils/fileUtils";
import {
  mapGalleryApiToCards,
  getGalleryCardDeleteImageUrl,
  postDeleteGalleryImages,
  type GalleryCardModel,
} from "../../../../../utils/galleryApiMap";
import {
  canManageTournamentGallery,
  findTournamentById,
} from "../../../../../utils/galleryManagePermission";
import { isLeagueAdmin } from "../../../../../utils/leagueAccess";
import type { ITournamentsByLeagueResponse } from "../../../types/home";

type GalleryItem = GalleryCardModel;

const Gallery = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tournamentId = sessionStorage.getItem("tournamentId");
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const isGuestModeRedux = useAppSelector((s) => s.authModal.isGuestMode);

  const [selectedGalleryItem, setSelectedGalleryItem] = useState<GalleryItem | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  const queryKey = [QUERY_KEY.getAllGalleryForTournaments, tournamentId];

  const { data: tournamentsByLeague } = useQuery({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, selectedLeagueId],
    queryFn: async () => {
      if (!selectedLeagueId) return null;
      const res = await Api.get<ITournamentsByLeagueResponse>(
        `${QUERY_KEY.getTournamentsByLeagues}/${selectedLeagueId}`,
      );
      return res?.data ?? null;
    },
    enabled: Boolean(selectedLeagueId && tournamentId),
  });

  const tournamentRow = useMemo(
    () => findTournamentById(tournamentsByLeague?.tournamentResponses, tournamentId),
    [tournamentsByLeague?.tournamentResponses, tournamentId],
  );

  const token = getAuthToken();
  const isGuestMode = isGuestModeRedux || getGuestModeFromStorage();
  const canManageGallery = Boolean(
    token &&
    !isGuestMode &&
    (isLeagueAdmin(selectedLeague) || canManageTournamentGallery(tournamentRow)),
  );

  useEffect(() => {
    if (!canManageGallery) {
      setIsDeleteMode(false);
      setSelectedIds([]);
    }
  }, [canManageGallery]);

  const { data: galleryItems = [], isLoading } = useQuery<GalleryItem[]>({
    queryKey,
    enabled: !!tournamentId,
    queryFn: async () => {
      if (!tournamentId) return [];
      const apiUrl = `getAllGalleryForPlayer/?tournamentId=${tournamentId}`;
      const response = await Api.get<unknown>(apiUrl);
      return mapGalleryApiToCards(response?.data, Images.gallery_match_image);
    },
  });

  const uploadGalleryMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const images = await Promise.all(
        files.map(async (file) => {
          const base64Url = await fileToBase64(file);
          const { data } = splitBase64(base64Url);
          return {
            base64Image: data,
            fileType: "PNG",
          };
        })
      );

      await Api.post(QUERY_KEY.multiplecreateGallery, {
        albumDate: null,
        albumName: null,
        imageType: "GALLERY_IMAGE",
        images,
        leagueId: null,
        teamSnapShotId: null,
        tournamentId: tournamentId?.trim() || null,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast.success("Gallery uploaded successfully.");
    },
    onError: (error) => {
      console.error("multipleCreateGallery failed:", error);
      toast.error("Failed to upload gallery.");
    },
  });

  const deleteGalleryMutation = useMutation({
    mutationFn: (urls: string[]) => postDeleteGalleryImages(urls),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      exitDeleteMode();
      toast.success("Selected photos removed.");
    },
    onError: (error) => {
      console.error("deleteGalleryImage failed:", error);
      toast.error("Failed to delete photos.");
    },
  });

  const generateImages = (item: GalleryItem): string[] => {
    if (item.images.length > 0) return item.images;
    if (item.imageUrl && item.imageUrl !== Images.gallery_match_image) {
      return [item.imageUrl];
    }
    return Array.from({ length: Math.max(1, item.photoCount) }, () => Images.gallery_match_image);
  };

  const handleCardClick = (item: GalleryItem) => {
    if (item.photoCount > 1) {
      navigate("/tournaments/tournaments-details/gallery-details", {
        state: { galleryItem: item },
      });
      return;
    }
    setSelectedGalleryItem(item);
    setSelectedImageIndex(0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedGalleryItem(null);
  };

  const handleUploadClick = () => {
    if (!canManageGallery) {
      toast.error("You do not have permission to add gallery photos.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canManageGallery) {
      toast.error("You do not have permission to add gallery photos.");
      event.target.value = "";
      return;
    }
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    event.target.value = "";
    await uploadGalleryMutation.mutateAsync(files);
  };

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggleSelection = (galleryId: string) => {
    setSelectedIds((prev) =>
      prev.includes(galleryId) ? prev.filter((id) => id !== galleryId) : [...prev, galleryId]
    );
  };

  const exitDeleteMode = () => {
    setIsDeleteMode(false);
    setSelectedIds([]);
  };

  const galleryRowId = (item: GalleryItem, index: number) =>
    String(item.matchId ?? `gallery-${index}`);

  const handleDelete = () => {
    if (!canManageGallery) {
      toast.error("You do not have permission to delete gallery photos.");
      return;
    }
    if (selectedIds.length === 0) return;
    const urls: string[] = [];
    galleryItems.forEach((item, idx) => {
      const rowId = galleryRowId(item, idx);
      if (!selectedIds.includes(rowId)) return;
      const u = getGalleryCardDeleteImageUrl(item, Images.gallery_match_image);
      if (u) urls.push(u);
    });
    const unique = [...new Set(urls)];
    if (unique.length === 0) {
      toast.error("No image URL available for the selected items. Try refreshing the page.");
      return;
    }
    if (unique.length < selectedIds.length) {
      toast("Some selections had no URL and were skipped.", { icon: "ℹ️" });
    }
    deleteGalleryMutation.mutate(unique);
  };

  return (
    <>
      <div className="gallery-wrapper">
        {canManageGallery && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
            onChange={handleFileChange}
          />
        )}
        <div className="gallery-header">
          {/* <h1 className="heading">Gallery</h1> */}
          {canManageGallery && (
            <div className="gallery-actions">
              <button
                type="button"
                className="gallery-btn gallery-btn--primary"
                onClick={handleUploadClick}
                disabled={uploadGalleryMutation.isPending || isLoading}
              >
                <FiUpload size={18} aria-hidden />
                Add
              </button>
              <button
                type="button"
                className="gallery-btn gallery-btn--secondary"
                onClick={() => (isDeleteMode ? exitDeleteMode() : setIsDeleteMode(true))}
              >
                {isDeleteMode ? (
                  "Cancel"
                ) : (
                  <>
                    <FiTrash2 size={18} aria-hidden />
                    Delete
                  </>
                )}
              </button>
              {isDeleteMode && (
                <button
                  type="button"
                  className="gallery-btn gallery-btn--danger"
                  onClick={handleDelete}
                  disabled={selectedIds.length === 0 || deleteGalleryMutation.isPending}
                >
                  <FiTrash2 size={18} aria-hidden />
                  Remove selected
                </button>
              )}
            </div>
          )}
        </div>

        {isLoading || uploadGalleryMutation.isPending ? (
          <div className="gallery-loading">Loading...</div>
        ) : galleryItems.length === 0 ? (
          <div className="no-data-found">
            <img src={Images.my_leagues_no} alt="No Gallery" />
            <p className="no-data-text">No photos found</p>
          </div>
        ) : (
          <>
            <div className="gallery-grid">
              {galleryItems.map((item, idx) => {
                const rowId = galleryRowId(item, idx);
                const selected = selectedSet.has(rowId);
                return (
                  <div
                    className={`gallery-card${selected ? " is-selected" : ""}`}
                    key={rowId}
                    onClick={() => {
                      if (isDeleteMode && canManageGallery) {
                        toggleSelection(rowId);
                      } else if (!isDeleteMode) {
                        handleCardClick(item);
                      }
                    }}
                  >
                    {isDeleteMode && canManageGallery && (
                      <span
                        className={`gallery-select${selected ? " is-selected" : ""}`}
                        aria-hidden
                      />
                    )}
                    <div className="gallery-img-wrapper">
                      <img
                        className="gallery-img"
                        src={item.imageUrl || Images.gallery_match_image}
                        alt={item.match}
                      />
                    </div>
                    <div className="gallery-overlay"></div>
                    <div className="gallery-content">
                      <div className="gallery-info">
                        <p className="gallery-match">{item.match}</p>
                        <p className="gallery-date">{item.date}</p>
                      </div>
                    </div>
                    <div className="gallery-badge">
                      <p className="gallery-count">{item.photoCount}</p>
                      <img className="gallery-icon" src={Images.camera_icon} alt="camera" />
                    </div>
                  </div>
                );
              })}
            </div>

            {isModalOpen && selectedGalleryItem && (
              <ImageZoomModal
                images={generateImages(selectedGalleryItem)}
                initialIndex={selectedImageIndex}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                altText={`${selectedGalleryItem.match} - Photo`}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Gallery;
